// Stats card model (A4/A5 — monorail-a3f/skv, revised after playtest round
// 1). Pure data builder for the specimen report: the four visible stats
// (Integrity / Locomotion / Attack / Guard) with named per-source
// contributions, plus the wardrobe — per-slot collections whose rows carry
// aligned label/value lines and D4-style deltas against what's worn.
// Adaptations/mutations deliberately absent: this is the gear-decision
// screen, and they have homes elsewhere (HUD tray, family album).
//
// Item contributions are PROBED — each equipped item's affixes are applied
// to a scratch Mods and diffed against identity — so a new affix shows up
// on the card without touching this file.

import type { Entity, ItemInstance, ItemSlot, Rarity } from './components'
import { emptyMods, recomputeMods, type Mods } from './mods'
import { AFFIX_DEFS, compareItem, equippedItem, type CompareLine } from './items'
import { equippedWeaponDef } from './weapons'
import { axopodDamageMult, flagellateSpeedMult, powerLevel } from './powers'
import { mutationMaxHpMult, mutationSpeedMult } from './mutations'
import { playerDamageMultForBiome } from './systems/damage'

export interface StatRow {
  label: string
  value: string
  /** Named contributions, only when non-identity ("Flagellate L2 +32%"). */
  sources: string[]
}

export interface WardrobeEntry {
  id: string
  name: string
  rarity: Rarity
  /** Aligned label/value rows; non-worn entries carry deltas vs the worn
   * item in the slot (worn rows compare against themselves — no deltas). */
  lines: CompareLine[]
  equipped: boolean
}

export interface StatsModel {
  stats: StatRow[]
  /** The full collection per slot, rarity-sorted, equipped flagged. */
  wardrobe: Record<ItemSlot, WardrobeEntry[]>
}

const pct = (f: number): string => `${f >= 0 ? '+' : '−'}${Math.round(Math.abs(f) * 100)}%`

/** Fold one item's affixes into a scratch Mods for probing. */
function probeItem(item: ItemInstance): Mods {
  const m = emptyMods()
  for (const roll of item.affixes) AFFIX_DEFS[roll.id]?.apply(m, roll.value)
  return m
}

/** Per-item multiplicative contribution to a channel, as "Name ±N%" lines. */
function itemMultSources(player: Entity, read: (m: Mods) => number): string[] {
  const out: string[] = []
  for (const slot of ['hat', 'weapon'] as ItemSlot[]) {
    const item = equippedItem(player, slot)
    if (!item) continue
    const f = read(probeItem(item))
    if (Math.abs(f - 1) > 1e-9) out.push(`${item.name} ${pct(f - 1)}`)
  }
  return out
}

export function buildStatsModel(player: Entity): StatsModel {
  const mods = recomputeMods(player)

  // ── Integrity ──────────────────────────────────────────────────────────────
  const hp = player.health
  const integritySources: string[] = []
  const envy = mutationMaxHpMult(player)
  if (envy !== 1) integritySources.push(`Nucleus envy ${pct(envy - 1)}`)
  integritySources.push(...itemMultSources(player, m => m.maxHpMult))

  // ── Locomotion ─────────────────────────────────────────────────────────────
  const locoSources: string[] = []
  const flag = powerLevel(player, 'flagellate')
  if (flag > 0) locoSources.push(`Flagellate L${flag} ${pct(flagellateSpeedMult(player) - 1)}`)
  const socks = mutationSpeedMult(player)
  if (socks !== 1) locoSources.push(`Wet socks ${pct(socks - 1)}`)
  locoSources.push(...itemMultSources(player, m => m.speedMult))

  // ── Attack ─────────────────────────────────────────────────────────────────
  const weapon = equippedWeaponDef(player)
  const damage = (weapon.damage + mods.attackDamageAdd) * mods.attackDamageMult
  const cadence = weapon.cooldown * mods.attackCooldownMult
  const attackSources: string[] = [`${weapon.name} ${weapon.damage.toFixed(2)}`]
  const axo = powerLevel(player, 'axopod')
  if (axo > 0) attackSources.push(`Axopod L${axo} ${pct(axopodDamageMult(player) - 1)}`)
  for (const slot of ['hat', 'weapon'] as ItemSlot[]) {
    const item = equippedItem(player, slot)
    if (!item) continue
    const m = probeItem(item)
    if (m.attackDamageAdd !== 0) attackSources.push(`${item.name} +${m.attackDamageAdd.toFixed(2)}`)
    if (m.attackDamageMult !== 1) attackSources.push(`${item.name} ${pct(m.attackDamageMult - 1)}`)
    if (m.attackCooldownMult !== 1) attackSources.push(`${item.name} ${pct(m.attackCooldownMult - 1)} cadence`)
  }

  // ── Guard ──────────────────────────────────────────────────────────────────
  const guard = 1 - mods.damageTakenMult
  // Sign flipped at the probe: less damage taken reads as more Guard.
  const guardSources: string[] = []
  for (const slot of ['hat', 'weapon'] as ItemSlot[]) {
    const item = equippedItem(player, slot)
    if (!item) continue
    const f = probeItem(item).damageTakenMult
    if (Math.abs(f - 1) > 1e-9) guardSources.push(`${item.name} ${pct(1 - f)}`)
  }
  // The under-tier biome tax is contextual, not gear — shown so a player deep
  // beyond their tier can SEE why hits are heavier (soft-gate legibility).
  const biomeTax = playerDamageMultForBiome(player)
  if (biomeTax > 1) guardSources.push(`biome tax ×${biomeTax.toFixed(1)}`)

  const stats: StatRow[] = [
    {
      label: 'Integrity',
      value: hp ? `${(Math.round(hp.current * 10) / 10)} / ${hp.max}` : '—',
      sources: integritySources,
    },
    { label: 'Locomotion', value: `×${mods.speedMult.toFixed(2)}`, sources: locoSources },
    {
      label: 'Attack',
      value: `${damage.toFixed(2)} / ${cadence.toFixed(2)}s`,
      sources: attackSources,
    },
    { label: 'Guard', value: pct(guard).replace('+', ''), sources: guardSources },
  ]

  // ── Wardrobe (A5) — rarity-sorted collection per slot ─────────────────────
  const RARITY_ORDER: Record<Rarity, number> = { unique: 0, pristine: 1, rare: 2, uncommon: 3 }
  const wardrobe: Record<ItemSlot, WardrobeEntry[]> = { hat: [], weapon: [] }
  for (const item of player.inventory ?? []) {
    const isWorn = player.equipped?.[item.slot] === item.id
    const wornInSlot = equippedItem(player, item.slot)
    wardrobe[item.slot].push({
      id: item.id,
      name: item.name,
      rarity: item.rarity,
      // Worn rows compare to themselves: plain values, no deltas.
      lines: compareItem(item, isWorn ? item : wornInSlot),
      equipped: isWorn,
    })
  }
  for (const slot of ['hat', 'weapon'] as ItemSlot[]) {
    wardrobe[slot].sort((a, b) =>
      RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity] || a.name.localeCompare(b.name))
  }

  return { stats, wardrobe }
}
